import { fetchAuthSession } from 'aws-amplify/auth'
import axios, { AxiosResponse } from 'axios'
import { getCognitoAccessToken } from './tokenUtils'
import { env } from './env'

// Token cache to avoid calling fetchAuthSession() on every request
interface TokenCache {
  token: string
  expiresAt: number
}

let tokenCache: TokenCache | null = null

/**
 * Get access token with caching to improve performance
 * Caches token in memory and only refreshes when expired or missing
 */
async function getAccessToken(): Promise<string | null> {
  const now = Date.now()

  // Return cached token if valid and not expired (with 1 minute buffer)
  if (tokenCache && tokenCache.expiresAt > now + 60000) {
    return tokenCache.token
  }

  // Try to get token from Amplify session
  try {
    const session = await fetchAuthSession({ forceRefresh: false })
    if (session.tokens?.accessToken) {
      const token = session.tokens.accessToken.toString()
      const expiration = session.tokens.accessToken.payload.exp
        ? session.tokens.accessToken.payload.exp * 1000 // Convert to milliseconds
        : now + 3600000 // Default 1 hour if no expiration

      // Cache the token
      tokenCache = {
        token,
        expiresAt: expiration,
      }

      return token
    }
  } catch (error) {
    // Log for debugging auth issues in development
    if (import.meta.env.DEV) {
      console.warn('Failed to fetch auth session, using localStorage fallback:', error)
    }
    // Clear invalid cache
    tokenCache = null
  }

  // Fallback: Try to get token directly from localStorage
  return getCognitoAccessToken()
}

function createInstance(baseUrl: string) {
  const baseConfig = { baseURL: baseUrl }
  const instance = axios.create(baseConfig)

  instance.interceptors.request.use(async (request) => {
    // Use Cognito auth token for all API calls (backend handles Sentry authentication)
    const accessToken = await getAccessToken()

    // Set Authorization header if we have a token
    if (accessToken) {
      request.headers.Authorization = `Bearer ${accessToken}`
    }

    // Only set Content-Type to application/json if it's not FormData
    // FormData needs to set its own Content-Type with boundary
    if (!(request.data instanceof FormData)) {
      request.headers['Content-type'] = 'application/json'
    }

    return request
  })

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      //  Removed automatic date parsing - let each hook handle dates explicitly via Zod schemas
      // This prevents:
      // - Recursive mutation of response objects
      // - Stack overflow from circular references
      // - Silent data corruption (e.g., date-like strings in titles)
      // - Type mismatches with Zod schemas
      return response
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  return instance
}

// Use API URL from environment variables
// In development, Vite proxy will handle /api and /logs requests
// In production, use the full API URL from environment
export const http = createInstance(import.meta.env.DEV ? '' : env.VITE_API_URL)
